import React, { useState, useEffect } from "react";
import web3 from './web3';
import Footer from './Footer';
import Navbar from './Navbar';
import parse from 'html-react-parser';
import './App.css';
import Swal from 'sweetalert2';
import ABI from './ABI.json';
import Chart from 'chart.js/auto';
import etcmcLogo from './etcmcLogo1.png';
import etcmcBackgroundLogo from './etcmcLogo3.png';
import ConnectWalletButton from './ConnectWalletButton';
import { db } from './firebase';
import { collection, addDoc, getDocs, setDoc, doc } from 'firebase/firestore';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const VotingApp = () => {
  const [contract, setContract] = useState(null);
  const [activeProposals, setActiveProposals] = useState([]);
  const [selectedProposalId, setSelectedProposalId] = useState(null);
  const [voteFor, setVoteFor] = useState(true);
  const [proposalDetailsMap, setProposalDetailsMap] = useState({});
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [proposalDescription, setProposalDescription] = useState("");
  const [voteCost, setVoteCost] = useState(null);
  const [proposalCost, setProposalCost] = useState(null);
  const [statisticsData, setStatisticsData] = useState({});
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [walletConnected, setWalletConnected] = useState(false);
  const [chartInstance, setChartInstance] = useState(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackContent, setFeedbackContent] = useState('');
  const [walletAddress, setWalletAddress] = useState('');
  const [feedbackList, setFeedbackList] = useState([]);
  const [showFeedbackListModal, setShowFeedbackListModal] = useState(false);
  const [showFullFeedback, setShowFullFeedback] = useState({});
  const [validationMessage, setValidationMessage] = useState('');
  const [previousResults, setPreviousResults] = useState([]);
  const [showPreviousResultsModal, setShowPreviousResultsModal] = useState(false);
  const [events, setEvents] = useState([]);
  const [showEventsWindow, setShowEventsWindow] = useState(true);

  useEffect(() => {
    const updateMetaDescription = (content) => {
      let metaTag = document.querySelector('meta[name="description"]');
      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.name = "description";
        document.head.appendChild(metaTag);
      }
      metaTag.content = content;
    };

    updateMetaDescription("ETCMC Voting Platform");

  }, []);
  useEffect(() => {
    const init = async () => {
      if (web3 && walletConnected) {
        try {
          const contractAddress = "0x266c0246EF902fA632d38e7d59e16Fca21269ceD";
          const contractABI = ABI;
          const contractInstance = new web3.eth.Contract(contractABI, contractAddress);
          setContract(contractInstance);

          loadActiveProposals(contractInstance);
          loadProposalCost(contractInstance);
          loadVoteCost(contractInstance);
          fetchProposalStatistics(contractInstance);

          await fetchPastEvents(contractInstance);

          contractInstance.events.ProposalSubmitted()
            .on('data', handleProposalCreated)
            .on('error', console.error);

          contractInstance.events.VoteCast()
            .on('data', handleVoteCast)
            .on('error', console.error);

          contractInstance.events.VotingEnded()
            .on('data', handleVotingEnded)
            .on('error', console.error);

        } catch (error) {
          console.error(error);
        }
      }
    };
    init();
  }, [walletConnected]);

  const loadActiveProposals = async (contractInstance) => {
    try {
      const activeIds = await contractInstance.methods.getActiveProposals().call();
      setActiveProposals(activeIds);
    } catch (error) {
      console.error(error);
    }
  };

  const loadProposalCost = async (contractInstance) => {
    try {
      const cost = await contractInstance.methods.proposalCost().call();
      setProposalCost(cost);
    } catch (error) {
      console.error(error);
    }
  };

  const loadVoteCost = async (contractInstance) => {
    try {
      const cost = await contractInstance.methods.voteCost().call();
      setVoteCost(cost);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (contract && activeProposals.length > 0) {
      const fetchProposalDetails = async () => {
        const detailsMap = {};
        for (const proposalId of activeProposals) {
          const details = await getProposalDetails(contract, proposalId);
          detailsMap[proposalId] = details;
        }
        setProposalDetailsMap(detailsMap);
      };
      fetchProposalDetails();
    }
  }, [contract, activeProposals]);

  const getProposalDetails = async (contractInstance, proposalId) => {
    setLoadingDetails(true);
    try {
      const proposal = await contractInstance.methods.proposals(proposalId).call();
      return proposal;
    } catch (error) {
      console.error(error);
      return null;
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleVote = async () => {
    if (!contract || !selectedProposalId) return;

    try {
      const accounts = await web3.eth.getAccounts();

      const transactionObject = {
        from: accounts[0],
        to: contract.options.address,
        data: contract.methods.vote(selectedProposalId, voteFor).encodeABI()
      };

      const approval = await window.ethereum.request({
        method: 'eth_sendTransaction',
        params: [transactionObject]
      });

      Swal.fire({
        icon: 'success',
        title: 'Transaction Submitted',
        html: 'Your vote has been submitted successfully. <br> Transaction Hash: ' + approval,
        confirmButtonText: 'OK'
      });

      loadActiveProposals(contract);
      fetchProposalStatistics(contract);
    } catch (error) {
      console.error('Error voting:', error);

      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'An error occurred while voting. Please try again later.',
        confirmButtonText: 'OK'
      });
    }
  };

  const handleProposalClick = async (proposalId) => {
    setSelectedProposalId(proposalId);
    setShowModal(true);
  };

  const handleCreateProposal = async () => {
    try {
      const accounts = await web3.eth.getAccounts();

      const transactionObject = {
        from: accounts[0],
        to: contract.options.address,
        data: contract.methods.submitProposal(proposalDescription).encodeABI(),
      };

      const approval = await window.ethereum.request({
        method: 'eth_sendTransaction',
        params: [transactionObject]
      });

      Swal.fire({
        icon: 'success',
        title: 'Proposal Created',
        html: 'Your proposal has been successfully created. <br> Transaction Hash: ' + approval,
        confirmButtonText: 'OK'
      }).then(() => {
        setTimeout(() => {
          window.location.reload();
        }, 20000);
      });

      setShowCreateModal(false);

      loadActiveProposals(contract);
      fetchProposalStatistics(contract);
    } catch (error) {
      console.error('Error creating proposal:', error);

      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'An error occurred while creating the proposal. Please try again later.',
        confirmButtonText: 'OK'
      });
    }
  };

  const fetchProposalStatistics = async (contractInstance) => {
    try {
      const proposalCount = await contractInstance.methods.nextProposalId().call();
      const statisticsData = {};

      for (let i = 1; i < Number(proposalCount); i++) {
        const proposal = await contractInstance.methods.proposals(i).call();
        const endTime = Number(proposal.endTime);
        const currentTime = Date.now() / 1000;
        if (proposal.exists && endTime > currentTime) {
          statisticsData[`Proposal ${i}`] = {
            forVotes: parseInt(proposal.forVotes, 10),
            againstVotes: parseInt(proposal.againstVotes, 10)
          };
        }
      }

      setStatisticsData(statisticsData);
      populateChart(statisticsData);
    } catch (error) {
      console.error('Error fetching proposal statistics:', error);
    }
  };

  const populateChart = (data) => {
    const ctx = document.getElementById('proposalChart').getContext('2d');
    if (chartInstance) {
      chartInstance.destroy();
    }
    const newChartInstance = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: Object.keys(data),
        datasets: [
          {
            label: 'Votes For',
            data: Object.values(data).map(proposal => proposal.forVotes),
            backgroundColor: 'rgba(0, 255, 0, 0.6)',
            borderColor: 'rgba(0, 255, 0, 1)',
            borderWidth: 1,
          },
          {
            label: 'Votes Against',
            data: Object.values(data).map(proposal => proposal.againstVotes),
            backgroundColor: 'rgba(255, 0, 0, 0.6)',
            borderColor: 'rgba(255, 0, 0, 1)',
            borderWidth: 1,
          }
        ]
      },
      options: {
        scales: {
          y: {
            beginAtZero: true
          }
        }
      }
    });
    setChartInstance(newChartInstance);
  };

  const handleFeedbackSubmit = async () => {
    try {
      if (!feedbackContent || !selectedProposalId || !walletAddress) {
        setValidationMessage("Wallet address is required.");
        return;
      }

      const feedbackData = {
        proposalId: selectedProposalId.toString(),
        feedback: feedbackContent,
        walletAddress: walletAddress
      };

      await addDoc(collection(db, "feedbacks"), feedbackData);
      setFeedbackList([...feedbackList, { id: feedbackList.length + 1, ...feedbackData }]);
      setFeedbackContent('');
      setWalletAddress('');
      setValidationMessage('');
      setShowFeedbackModal(false);
      loadFeedbacks(selectedProposalId);
    } catch (e) {
      console.error("Error adding document: ", e);
    }
  };

  const loadFeedbacks = async (proposalId) => {
    try {
      const feedbackCollection = collection(db, "feedbacks");
      const feedbackSnapshot = await getDocs(feedbackCollection);
      const feedbackList = feedbackSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(feedback => feedback.proposalId === proposalId.toString());

      setFeedbackList(feedbackList);
      setShowFeedbackListModal(true);
    } catch (error) {
      console.error("Error loading feedbacks: ", error);
    }
  };

  const handleShowFeedback = (proposalId) => {
    setSelectedProposalId(proposalId);
    loadFeedbacks(proposalId);
  };

  const formatEndTime = (endTime) => {
    const milliseconds = Number(endTime) * 1000;
    const date = new Date(milliseconds);
    return date.toLocaleString();
  };

  const renderShortenedDescription = (description) => {
    const maxLength = 100;
    return description.length > maxLength ? description.substring(0, maxLength) + "..." : description;
  };

  const renderFullDescription = (description) => {
    return <div>{parse(description)}</div>;
  };

  const handleViewDetails = (proposalId) => {
    setSelectedProposalId(proposalId);
    setShowModal(true);
  };

  const toggleShowFullDescription = () => {
    setShowFullDescription(!showFullDescription);
  };

  const toggleShowFullFeedback = (feedbackId) => {
    setShowFullFeedback(prevState => ({
      ...prevState,
      [feedbackId]: !prevState[feedbackId]
    }));
  };

  const handleProposalCreated = (event) => {
    console.log("ProposalCreated event received:", event);
    const { id } = event.returnValues;
    addEvent({ type: "Proposal Created", id: id.toString() });
  };

  const handleVoteCast = (event) => {
    console.log("VoteCast event received:", event);
    const { proposalId, inSupport } = event.returnValues;
    addEvent({ type: "Vote Cast", id: proposalId.toString(), inSupport });
  };

  const handleVotingEnded = async (event) => {
    console.log("VotingEnded event received:", event);
    const { proposalId } = event.returnValues;
    const proposal = await getProposalDetails(contract, proposalId);
    console.log("Proposal details for ended vote:", proposal);
    await storeProposalResult(proposalId, proposal);
    fetchProposalStatistics(contract);
  };

  const storeProposalResult = async (proposalId, proposal) => {
    const proposalData = {
      proposalId: proposalId.toString(),
      description: proposal.description || "No description available",
      forVotes: proposal.forVotes.toString(),
      againstVotes: proposal.againstVotes.toString(),
      totalVotes: proposal.totalVotes.toString(),
      endTime: proposal.endTime.toString()
    };

    try {
      console.log("Storing proposal result in Firestore:", proposalData);
      await setDoc(doc(db, "proposalResults", proposalId.toString()), proposalData);
      console.log("Proposal result stored successfully");
    } catch (error) {
      console.error("Error storing proposal result:", error);
    }
  };

  const fetchPastEvents = async (contractInstance) => {
    console.log("Fetching past VotingEnded events");
    try {
      const pastEvents = await contractInstance.getPastEvents('VotingEnded', {
        fromBlock: 0,
        toBlock: 'latest'
      });
      for (const event of pastEvents) {
        console.log("Past VotingEnded event:", event);
        const { proposalId } = event.returnValues;
        const proposal = await getProposalDetails(contractInstance, proposalId);
        await storeProposalResult(proposalId, proposal);
      }
      console.log("Past VotingEnded events processed");
    } catch (error) {
      console.error("Error fetching past VotingEnded events:", error);
    }

    try {
      const pastEvents = await contractInstance.getPastEvents('ProposalSubmitted', {
        fromBlock: 0,
        toBlock: 'latest'
      });
      for (const event of pastEvents) {
        handleProposalCreated(event);
      }
    } catch (error) {
      console.error("Error fetching past ProposalSubmitted events:", error);
    }

    try {
      const pastEvents = await contractInstance.getPastEvents('VoteCast', {
        fromBlock: 0,
        toBlock: 'latest'
      });
      for (const event of pastEvents) {
        handleVoteCast(event);
      }
    } catch (error) {
      console.error("Error fetching past VoteCast events:", error);
    }
  };

  const handlePreviousResults = async () => {
    console.log("Fetching previous proposal results from Firestore");
    try {
      const resultsSnapshot = await getDocs(collection(db, "proposalResults"));
      const resultsList = resultsSnapshot.docs.map(doc => doc.data());

      const validResults = resultsList.filter(result => result.proposalId && result.description && result.forVotes && result.againstVotes && result.totalVotes && result.endTime);
      
      console.log("Previous proposal results fetched:", validResults);
      setPreviousResults(validResults);
      setShowPreviousResultsModal(true);
    } catch (error) {
      console.error("Error loading previous results:", error);
      setPreviousResults([]);
      setShowPreviousResultsModal(true);
    }
  };

  const calculateResult = (forVotes, againstVotes) => {
    return forVotes > againstVotes ? "Passed" : "Failed";
  };

  const addEvent = (newEvent) => {
    setEvents(prevEvents => {
      const updatedEvents = [newEvent, ...prevEvents];
      return updatedEvents.slice(0, 5);
    });
  };

  const handleCloseEventsWindow = () => {
    setShowEventsWindow(false);
  };

  const handleShowEventsWindow = () => {
    setShowEventsWindow(true);
  };

  return (
    <div className="container">
      <header className="header">
        <img src={etcmcLogo} className="App-logo" alt="logo" />
        <h1 className="header-title">ETCMC DAO</h1>
        <div className="connect-wallet-container">
          <ConnectWalletButton onConnect={setWalletConnected} />
        </div>
      </header>
      <div className="separator-line"></div>
      <h2 className="subheading">Welcome to the ETCMC DAO Voting platform</h2>
      <Navbar
        showCreateModal={showCreateModal}
        setShowCreateModal={setShowCreateModal}
        proposalDescription={proposalDescription}
        setProposalDescription={setProposalDescription}
        handleCreateProposal={handleCreateProposal}
        proposalCost={proposalCost}
        voteCost={voteCost}
        handleShowFeedback={handleShowFeedback}
        handlePreviousResults={handlePreviousResults}
      />

      <div className="proposal-list">
        {activeProposals.map((proposalId) => (
          <div className="proposal-item" key={proposalId}>
            <div className="proposal-content">
              <h3>Proposal ID: {proposalId.toString()}</h3>
              <p>Description: {parse(renderShortenedDescription(proposalDetailsMap[proposalId] ? proposalDetailsMap[proposalId].description : 'Loading...'))}</p>
              <p>End Time: {proposalDetailsMap[proposalId] ? formatEndTime(proposalDetailsMap[proposalId].endTime) : 'Loading...'}</p>
              <button className="view-details" onClick={() => handleViewDetails(proposalId)}>
                View Details
              </button>
              <button className="view-feedback" onClick={() => setShowFeedbackModal(true)}>Leave Feedback</button>
              <button className="view-feedback" onClick={() => handleShowFeedback(proposalId)}>Show Feedback</button>
            </div>
          </div>
        ))}
      </div>
      {showModal && selectedProposalId && (
        <div className="modal">
          <div className="modal-content larger-modal-content"> {/* Added a new class for larger modal */}
            <span className="close" onClick={() => setShowModal(false)}>&times;</span>
            <h2>Proposal Details:</h2>
            {loadingDetails ? (
              <p>Loading...</p>
            ) : (
              proposalDetailsMap[selectedProposalId] && (
                <>
                  <p>Description: <span>{showFullDescription ? renderFullDescription(proposalDetailsMap[selectedProposalId].description) : parse(renderShortenedDescription(proposalDetailsMap[selectedProposalId].description))}</span></p>
                  {proposalDetailsMap[selectedProposalId].description.length > 100 && (
                  <button className="show-toggle" onClick={toggleShowFullDescription}>
                    {showFullDescription ? 'Show Less' : 'Show More'}
                  </button>
                )}

                  <p>Votes For: {proposalDetailsMap[selectedProposalId].forVotes.toString()}</p>
                  <p>Votes Against: {proposalDetailsMap[selectedProposalId].againstVotes.toString()}</p>
                  <p>Total Votes: {proposalDetailsMap[selectedProposalId].totalVotes.toString()}</p>
                  <p>Exists: {proposalDetailsMap[selectedProposalId].exists ? "Yes" : "No"}</p>
                  <p>End Time: {formatEndTime(proposalDetailsMap[selectedProposalId].endTime)}</p>
                </>
              )
            )}
            <div className="vote-section">
              <h3>Vote for Proposal {selectedProposalId}</h3>
              <label>
                <input
                  type="radio"
                  value="for"
                  checked={voteFor}
                  onChange={() => setVoteFor(true)}
                />
                Vote For
              </label>
              <label>
                <input
                  type="radio"
                  value="against"
                  checked={!voteFor}
                  onChange={() => setVoteFor(false)}
                />
                Vote Against
              </label>
              <button className="submit-vote" onClick={handleVote}>Submit Vote</button>
            </div>
          </div>
        </div>
      )}

      {showFeedbackModal && (
        <div className="modal feedback-modal">
          <div className="modal-content">
            <span className="close" onClick={() => setShowFeedbackModal(false)}>&times;</span>
            <h2>Leave Feedback</h2>
            <input
              type="text"
              placeholder="Enter your wallet address"
              value={walletAddress}
              onChange={(e) => setWalletAddress(e.target.value)}
              style={{ marginBottom: '20px' }}
              required
            />
            {validationMessage && <p className="validation-message">{validationMessage}</p>}
            <ReactQuill
              value={feedbackContent}
              onChange={setFeedbackContent}
            />
            <button className="button" onClick={handleFeedbackSubmit}>Submit</button>
          </div>
        </div>
      )}

      {showFeedbackListModal && (
        <div className="modal feedback-list-modal">
          <div className="modal-content">
            <span className="close" onClick={() => setShowFeedbackListModal(false)}>&times;</span>
            <h2>Community Feedback for Proposal {selectedProposalId}</h2>
            <ul>
              {feedbackList.map(feedback => (
                <li key={feedback.id}>
                  <h3>Proposal ID: {feedback.proposalId}</h3>
                  <div>
                    {showFullFeedback[feedback.id] ? (
                      <div dangerouslySetInnerHTML={{ __html: feedback.feedback }}></div>
                    ) : (
                      <div dangerouslySetInnerHTML={{ __html: feedback.feedback.substring(0, 100) }}></div>
                    )}
                    {feedback.feedback.length > 100 && (
                      <button onClick={() => toggleShowFullFeedback(feedback.id)}>
                        {showFullFeedback[feedback.id] ? 'Show Less' : 'Show More'}
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
            <button className="button" onClick={() => setShowFeedbackListModal(false)}>Close</button>
          </div>
        </div>
      )}

      {showPreviousResultsModal && (
        <div className="modal previous-results-modal">
          <div className="modal-content">
            <span className="close" onClick={() => setShowPreviousResultsModal(false)}>&times;</span>
            <h2>Previous Proposal Results</h2>
            <ul>
              {previousResults.length > 0 ? (
                previousResults.map(result => (
                  <li key={result.proposalId}>
                    <h3>Proposal ID: {result.proposalId}</h3>
                    <p>Description: {result.description ? parse(result.description) : "No description available"}</p>
                    <p>For Votes: {result.forVotes}</p>
                    <p>Against Votes: {result.againstVotes}</p>
                    <p>Total Votes: {result.totalVotes}</p>
                    <p>End Time: {result.endTime}</p>
                    <p>Result: {calculateResult(result.forVotes, result.againstVotes)}</p>
                  </li>
                ))
              ) : (
                <li>No previous results available</li>
              )}
            </ul>
            <button className="button" onClick={() => setShowPreviousResultsModal(false)}>Close</button>
          </div>
        </div>
      )}

      <Footer />

      <div className="chart-container">
        <h3>Proposal Statistics</h3>
        <canvas id="proposalChart"></canvas>
      </div>

      <div className="background-logo-container">
        <img src={etcmcBackgroundLogo} className="background-logo" alt="background logo" />
      </div>

      {showEventsWindow ? (
        <div className="events-window">
          <h3>Live Events <span className="close" onClick={handleCloseEventsWindow}>&times;</span></h3>
          <ul>
            {events.map((event, index) => (
              <li key={index}>
                <strong>{event.type}</strong> - Proposal ID: {event.id}
                {event.description && event.type !== "Proposal Created" && <p>{parse(event.description)}</p>}
                {event.inSupport !== undefined && <p>Support: {event.inSupport ? "Yes" : "No"}</p>}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <button className="show-events-button" onClick={handleShowEventsWindow}>Show Events</button>
      )}
    </div>
  );
};

export default VotingApp;